// HACK: Okay, so EXIF is ruining everything
//
// If a user has an image that has an EXIF orientation set, then the list
// of previews might have some images that are oriented the "right" way
// regardless of if EXIF orientation is respected (because the server
// auto-oriented them as part of the scaling process) and the original
// image, which will only be oriented correctly if EXIF orientation is
// respected … which it *won't* be if it's being displayed in HTML by a
// browser (however, Chrome at least will respect EXIF if you're using
// the browser to view the image directly)
//
// So, rather than wait for image-orientation: from-image to become a thing
// we can mitigate the problem by removing the one image with a width/height
// that gives it an aspect ratio unlike the rest of the previews.  Of course,
// this isn't a complete solution because it only works if there were at least
// two previews generated … but that's better than things just looking broken.

export interface Preview {
  scaled?: boolean;
  width: number;
  height: number;
  url?: string;
}

export function tryToExcludeEXIFRotated(
  previews: Preview[] | null = [],
): Preview[] {
  if (!previews) {
    return [];
  }

  // Try to figure out the most common orientation.
  const portraitLevel = previews.reduce(function(
    memo,
    { scaled, width, height },
  ) {
    // It's possible for scaled down versions of a non-square image to
    // end up with dimensions that are square (due to rounding)  Don't use
    // those to determine the actual orientation
    if (!scaled || width === height) {
      return memo;
    } else if (height > width) {
      return memo + 1;
    } else {
      return memo - 1;
    }
  },
  0);

  const usePortrait = portraitLevel > 0;

  return previews.filter(function({ width, height }) {
    if (usePortrait) {
      return height >= width;
    } else {
      return width >= height;
    }
  });
}

export function preferScaledPreviews(
  previews: Preview[] | null = [],
): Preview[] {
  if (!previews) {
    return [];
  }

  const scaledPreviews = tryToExcludeEXIFRotated(
    previews.filter((p) => p.scaled),
  );

  if (scaledPreviews && scaledPreviews.length) {
    return scaledPreviews;
  } else {
    return previews;
  }
}

export function biggestPreview(previews: Preview[] | null = []) {
  if (!previews || previews.length === 0) {
    return null;
  } else {
    return preferScaledPreviews(previews).sort(
      (a, b) => Math.min(b.width, b.height) - Math.min(a.width, a.height),
    )[0];
  }
}

export function smallestPreview(previews: Preview[] | null = []) {
  if (!previews || previews.length === 0) {
    return null;
  } else {
    return preferScaledPreviews(previews).sort(
      (a, b) => Math.max(a.width, a.height) - Math.max(b.width, b.height),
    )[0];
  }
}

export function smallestPreviewBiggerThan(
  previews: Preview[] | null = [],
  width: number = 0,
  height: number = 0,
) {
  if (!previews) {
    return null;
  }

  let eligible;
  let previewSet;
  const previewSets = [
    preferScaledPreviews(previews),
    tryToExcludeEXIFRotated(previews),
  ];
  for (previewSet of previewSets) {
    if (!eligible) {
      eligible = previewSet.filter(
        (p) => p.width >= width && p.height >= height,
      );
    }
  }

  return smallestPreview(eligible);
}

function previewsBetween(
  previews: Preview[] | null = [],
  minWidth: number,
  minHeight: number,
  maxWidth: number,
  maxHeight: number,
) {
  return preferScaledPreviews(previews).filter(
    (p) =>
      minWidth <= p.width &&
      p.width <= maxWidth &&
      (minHeight <= p.height && p.height <= maxHeight),
  );
}

export function previewBetween(
  previews: Preview[] | null = [],
  minWidth: number,
  minHeight: number,
  maxWidth: number,
  maxHeight: number,
) {
  return biggestPreview(
    previewsBetween(previews, minWidth, minHeight, maxWidth, maxHeight),
  );
}

export function smallestPreviewBetween(
  previews: Preview[] | null = [],
  minWidth: number,
  minHeight: number,
  maxWidth: number,
  maxHeight: number,
) {
  return smallestPreview(
    previewsBetween(previews, minWidth, minHeight, maxWidth, maxHeight),
  );
}



// WEBPACK FOOTER //
// ./app/common/lib/util/preview.ts